import {
  getPlace,
  setPlace,
  getPreferredBoutique,
  setPreferredBoutique,
} from '@js/utils/clickCollectStorage';
import { fetchAllowedBoutiqueIds } from '@js/utils/clickCollectAllowedBoutiques';
import { runAddToCart } from '@js/actions/runAddToCart';
import {
  transformHyperspaceStoreData,
  filterInStockOnly,
  sortPreferredFirst,
} from '@js/utils/transformHyperspaceStoreData';

export default {
  name: 'clickCollectDrawer',
  component() {
    return {
      isOpen: false,
      loading: false,
      error: null,
      mode: 'pdp',
      skus: [],
      variantId: null,
      variantSku: null,
      quantity: 1,
      addingToCart: false,
      place: null,
      placeInput: '',
      usingCurrentLocation: false,
      boutiques: [],
      visibleCount: 3,
      showAll: false,
      showInStockOnly: false,
      preferredBoutiqueId: null,
      expandedBoutiqueId: null,
      autocomplete: null,
      _selectedBoutiqueId: null,

      get config() {
        return window.theme?.clickAndCollect || {};
      },

      get displayedBoutiques() {
        let list = this.boutiques;
        if (this.showInStockOnly) {
          list = filterInStockOnly(list);
        }
        return this.showAll ? list : list.slice(0, this.visibleCount);
      },

      get hasMoreBoutiques() {
        const list = this.showInStockOnly ? filterInStockOnly(this.boutiques) : this.boutiques;
        return list.length > this.visibleCount && !this.showAll;
      },

      get addToCartLabel() {
        return window.variantStrings?.addToCart || 'Add to cart';
      },

      get canAddToCartFromDrawer() {
        if (this.mode !== 'pdp' || !this.variantId || this.addingToCart) return false;
        const boutique = this.getSelectedBoutiqueObject();
        return Boolean(boutique && boutique.aggregateStatus !== 'unavailable');
      },

      init() {
        this.place = getPlace();
        this.placeInput = this.place?.address || this.place?.formattedAddress || '';
        this.preferredBoutiqueId = getPreferredBoutique()?.id || null;

        this._openHandler = (event) => this.openFromEvent(event);
        this._closeHandler = () => this.close();
        this._mapsReadyHandler = () => this.initAutocomplete();

        window.addEventListener('click-collect:open', this._openHandler);
        window.addEventListener('click-collect:close', this._closeHandler);
        window.addEventListener('click-collect:maps-ready', this._mapsReadyHandler);

        this._keyHandler = (e) => {
          if (e.key === 'Escape' && this.isOpen) this.close();
        };
        document.addEventListener('keydown', this._keyHandler);

        if (window.google?.maps?.places) {
          this.initAutocomplete();
        }
      },

      destroy() {
        window.removeEventListener('click-collect:open', this._openHandler);
        window.removeEventListener('click-collect:close', this._closeHandler);
        window.removeEventListener('click-collect:maps-ready', this._mapsReadyHandler);
        document.removeEventListener('keydown', this._keyHandler);
        document.body.classList.remove('no-scroll');
      },

      openFromEvent(event) {
        const detail = event?.detail || {};
        this.mode = detail.mode || 'pdp';
        this.skus = detail.skus || [];
        this.variantId = detail.variantId || null;
        this.variantSku = detail.variantSku || this.skus[0] || null;
        this.quantity = detail.quantity || 1;
        this._selectedBoutiqueId = null;
        this.addingToCart = false;

        if (!this.skus.length && this.mode === 'cart' && this.$store.cart?.items) {
          this.skus = this.$store.cart.items.map((item) => item.sku).filter(Boolean);
        }

        this.open();
      },

      open() {
        this.isOpen = true;
        this.error = null;
        document.body.classList.add('no-scroll');

        this.$nextTick(() => {
          this.initAutocomplete();
          if (this.place?.lat != null && this.place?.lng != null) {
            this.fetchBoutiques();
          }
        });
      },

      close() {
        this.isOpen = false;
        document.body.classList.remove('no-scroll');
      },

      initAutocomplete() {
        const input = this.$refs.placeInput;
        if (!input || !window.google?.maps?.places) return;

        if (this.autocomplete) return;

        try {
          this.autocomplete = new window.google.maps.places.Autocomplete(input, {
            fields: ['geometry', 'formatted_address', 'name'],
          });

          this.autocomplete.addListener('place_changed', () => {
            const placeResult = this.autocomplete.getPlace();
            const location = placeResult?.geometry?.location;
            if (!location) return;
            this.usingCurrentLocation = false;
            this.place = {
              lat: location.lat(),
              lng: location.lng(),
              address: placeResult.formatted_address || placeResult.name || '',
              formattedAddress: placeResult.formatted_address || '',
            };
            this.placeInput = this.place.address;
            setPlace(this.place);
            this.fetchBoutiques();
          });
        } catch (error) {
          this.error = 'Location search is unavailable. Use current location or enter coordinates manually.';
        }
      },

      useCurrentLocation() {
        if (!navigator.geolocation) {
          this.error = 'Geolocation is not supported by your browser.';
          return;
        }

        this.loading = true;
        this.error = null;

        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.place = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              address: 'Current location',
              formattedAddress: 'Current location',
            };

            this.usingCurrentLocation = true;
            this.placeInput = '';
            setPlace(this.place);
            this.fetchBoutiques();
          },
          () => {
            this.loading = false;
            this.error = 'Unable to retrieve your current location.';
          },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      },

      async fetchBoutiques() {
        if (!this.config.enabled || !this.config.apiEndpointUrl) {
          this.error = 'Click & Collect is not enabled.';
          return;
        }

        if (this.place?.lat == null || this.place?.lng == null) {
          this.error = 'Please enter a location first.';
          return;
        }

        if (!this.skus.length) {
          this.error = 'No product SKUs available to check.';
          return;
        }

        this.loading = true;
        this.error = null;

        try {
          const allowedBoutiqueIds = await fetchAllowedBoutiqueIds();

          const requestBody = {
            position: { lat: this.place.lat, lng: this.place.lng },
            query: { items: this.skus },
            limit: 20,
          };

          const response = await fetch(this.config.apiEndpointUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
          });


          if (!response.ok) {
            throw new Error(`Store lookup failed (${response.status})`);
          }

          const raw = await response.json();

          let boutiques = transformHyperspaceStoreData(raw, {
            allowedBoutiqueIds,
            skus: this.skus,
            warehouseTransferEnabled: this.config.warehouseTransferEnabled,
            config: this.config,
          });

          boutiques = sortPreferredFirst(boutiques, getPreferredBoutique());
          this.boutiques = boutiques;
          this.showAll = false;
          const firstBoutique = boutiques[0] || null;
          this.expandedBoutiqueId = firstBoutique?.id || null;
          if (this.mode === 'pdp' && firstBoutique?.aggregateStatus !== 'unavailable') {
            this._selectedBoutiqueId = firstBoutique.id;
          }
        } catch (err) {
          this.error = err.message || 'Unable to load store availability.';
          this.boutiques = [];
        } finally {
          this.loading = false;
        }
      },

      togglePreferred(boutique, checked) {
        if (checked) {
          setPreferredBoutique(boutique);
          this.preferredBoutiqueId = boutique.id;
          this.boutiques = sortPreferredFirst(this.boutiques, boutique);
        } else {
          setPreferredBoutique(null);
          this.preferredBoutiqueId = null;
        }
      },

      isPreferred(boutique) {
        return String(this.preferredBoutiqueId) === String(boutique.id);
      },

      pickBoutique(boutique) {
        if (this.mode !== 'pdp' || boutique?.aggregateStatus === 'unavailable') return;
        this._selectedBoutiqueId = boutique.id;
      },

      getSelectedBoutiqueObject() {
        if (!this._selectedBoutiqueId) return null;
        return this.boutiques.find(
          (boutique) => String(boutique.id) === String(this._selectedBoutiqueId)
        ) || null;
      },

      async selectBoutique(boutique) {
        const store = this.$store.clickCollect;
        if (!store) return;

        if (this.mode === 'pdp') {
          this.pickBoutique(boutique);
          return;
        }

        await store.applyBoutiqueSelection(boutique, this.skus);
        this._selectedBoutiqueId = boutique.id;
        this.close();
      },

      async addToCartFromDrawer() {
        const boutique = this.getSelectedBoutiqueObject();
        if (!boutique || !this.variantId || this.addingToCart) return;

        this.addingToCart = true;
        this.error = null;

        try {
          const result = await runAddToCart({
            variantId: this.variantId,
            variantSku: this.variantSku,
            skus: this.skus,
            quantity: this.quantity,
            boutique,
          });

          if (result.success) {
            this.close();
            return;
          }

          this.error = result.error || 'Unable to add to cart.';
        } catch (err) {
          this.error = err.message || 'Unable to add to cart.';
        } finally {
          this.addingToCart = false;
        }
      },

      isSelectedBoutique(boutique) {
        if (this._selectedBoutiqueId) {
          return String(this._selectedBoutiqueId) === String(boutique.id);
        }
        const store = this.$store.clickCollect;
        if (!store) return false;
        const selected = store.selectedBoutique || store.pdpPickupBoutique;
        return selected ? String(selected.id) === String(boutique.id) : false;
      },

      toggleExpanded(boutiqueId) {
        const isClosing = this.expandedBoutiqueId === boutiqueId;
        this.expandedBoutiqueId = isClosing ? null : boutiqueId;

        if (this.mode === 'pdp' && !isClosing) {
          const boutique = this.boutiques.find(
            (item) => String(item.id) === String(boutiqueId)
          );
          if (boutique) this.pickBoutique(boutique);
        }
      },

      isExpanded(boutique) {
        return this.expandedBoutiqueId === boutique.id;
      },

      getSkuStatusLabel(skuStatus) {
        if (!skuStatus) return 'Unavailable';
        if (skuStatus.status === 1) return 'Available';
        if (skuStatus.status === 2 && skuStatus.quantity > 0) return 'Low Stock';
        return 'Unavailable';
      },

      getSkuStatusForBoutique(boutique, sku) {
        const statuses = boutique?.skuStatuses || [];
        return statuses.find((item) => item.sku === sku) || null;
      },

      formatBoutiqueName(name) {
        if (!name) return '';
        const idx = name.indexOf(': ');
        return idx !== -1 ? name.slice(idx + 2) : name;
      },

      formatDistance(distance) {
        if (distance == null || distance === '') return '';
        const km = parseFloat(distance);
        if (isNaN(km)) return '';
        return `${km.toFixed(2)}km`;
      },

      formatEta(eta) {
        if (!eta) return '';
        return `Estimated pickup: ${eta}`;
      },

      formatAddress(boutique) {
        if (!boutique?.address) return '';
        if (typeof boutique.address === 'string') return boutique.address;
        const addr = boutique.address;
        const street = addr.line1 || addr.address1 || addr.street || '';
        const city = addr.city || '';
        const state = addr.state || addr.province || '';
        const zip = addr.postcode || addr.zip || addr.postal_code || '';
        const cityLine = [city, state, zip].filter(Boolean).join(' ');
        return [street, cityLine].filter(Boolean).join(', ');
      },

      /**
       * Parse open_hours string like "MONDAY: 9.00AM - 5.30PM\nTUESDAY: 9.00AM - 5.30PM"
       * into [{day: 'Monday', time: '9.00AM - 5.30PM'}, ...]
       */
      parseOpenHours(hoursStr) {
        if (!hoursStr || typeof hoursStr !== 'string') return [];
        return hoursStr
          .split(/\n/)
          .map(line => line.trim())
          .filter(Boolean)
          .map(line => {
            const colonIdx = line.indexOf(':');
            if (colonIdx === -1) return { day: line, time: '' };
            const day = line.slice(0, colonIdx).trim();
            const time = line.slice(colonIdx + 1).trim();
            // Title-case the day name (e.g. MONDAY → Monday)
            const dayFormatted = day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
            return { day: dayFormatted, time };
          });
      },

      statusClass(boutique) {
        return `is-${boutique?.aggregateStatus || 'unavailable'}`;
      },
    };
  },
};
